import { getArcade, arcadeEndpoint } from "./arcade";
import { playableGames } from "../games/registry";

export type PushSetupState = "unsupported" | "off" | "on";

export function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const decoded = atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
}

function supported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function getPushSetupState(): Promise<PushSetupState> {
  if (!supported()) return "unsupported";
  if (Notification.permission !== "granted") return "off";
  const registration = await navigator.serviceWorker.ready;
  return (await registration.pushManager.getSubscription()) ? "on" : "off";
}

export async function enablePushNotifications(displayName?: string): Promise<void> {
  if (!supported()) throw new Error("Push notifications aren’t supported in this browser.");

  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notification permission was not granted.");

  const registration = await navigator.serviceWorker.ready;
  const keyResponse = await fetch(`${arcadeEndpoint}/push/vapid-public-key`);
  if (!keyResponse.ok) throw new Error("Turn alerts are not available on the game server yet.");
  const { publicKey } = await keyResponse.json() as { publicKey?: string };
  if (!publicKey) throw new Error("The game server did not provide a push key.");

  const subscription = await registration.pushManager.getSubscription()
    ?? await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });

  try {
    await Promise.all(playableGames().map(async ({ gameId }) => {
      const arcade = await getArcade(gameId, displayName);
      const response = await fetch(`${arcadeEndpoint}/push/subscriptions`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${await arcade.token()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ gameId, subscription: subscription.toJSON() }),
      });
      if (!response.ok) throw new Error(`Push registration failed (${response.status}).`);
    }));
  } catch (error) {
    await subscription.unsubscribe();
    throw error;
  }
}
